import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { lazy, useContext } from 'react';
import { DataManagementApp } from 'apps.data_management';
import { ECOABuilderApp } from 'apps.ecoa_builder';
import { SiteApp } from 'apps.site_app';
import { SiteManagementApp } from 'apps.site_management';
import { StudyManagementApp } from 'apps.study_management';
import { UserManagementApp } from 'apps.user_management';
import { FeatureFlagsContext } from 'libs.flags.react';
import { useLocalizeMessage } from 'libs.nucleus';
import { AclScope, MedableAIIcon } from 'libs.react';
import { useScopeValidator, useUserRoles } from 'libs.react_internal';
import DeveloperFlagsPage from '../../pages/developer_flags/developer_flags.component';
const WorkspaceSettingsPage = lazy(() => import('../../pages/workspace/workspace_settings.page'));
const ProfilePage = lazy(() => import('../../pages/profile/profile.page'));
const NotificationsPage = lazy(() => import('../../pages/notifications/notifications.page'));
export const useSelfServiceNavigation = () => {
    const { isAdmin, isBuilder, isLibrarian, isUserAndSiteManager, isSiteUser, isDataUser, isDataAdmin, isAIAdmin, isAIBuilder, isAIUser, } = useUserRoles();
    const translate = useLocalizeMessage();
    const { isFeatureEnabled } = useContext(FeatureFlagsContext);
    const isAgentsAppEnabled = isFeatureEnabled('AgentsApp');
    const canUpdateWorkspace = useScopeValidator(AclScope.MOM_ENTITY_UPDATE);
    const hasAIAccess = isAdmin || isAIAdmin || isAIBuilder || isAIUser;
    const appRoutes = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, ((isAdmin || isBuilder || isUserAndSiteManager || isDataUser || isSiteUser) && {
        studies: {
            name: 'Studies',
            path: '/studies',
            redirectTo: 'dashboard',
            hideSideNav: true,
            canBeDefaultPage: true,
            /*
              We only load the StudyManagementApp to enable report details page route,
              but site users actually should not see or have access to it, this should be a
              temporary solution until Lumos gets the shared reports package to enable this route
              which will happen in URP-275
            */
            hideFromHeader: isSiteUser,
            appRoutes: [{ title: 'Studies', path: '/studies', component: StudyManagementApp }],
        },
    })), ((isAdmin || isLibrarian || isBuilder) && {
        ecoa: {
            name: 'Library',
            path: '/library',
            redirectTo: 'activities',
            hideSideNav: true,
            canBeDefaultPage: true,
            appRoutes: [{ title: 'Activities', path: 'activities', component: ECOABuilderApp, icon: 'navProtocol' }],
        },
    })), ((isAdmin || isUserAndSiteManager) && {
        users: {
            name: 'Users',
            path: '/users',
            redirectTo: 'dashboard',
            hideSideNav: true,
            canBeDefaultPage: true,
            appRoutes: [{ title: 'Users', path: '/users', component: UserManagementApp }],
        },
    })), (isSiteUser && {
        site: {
            name: translate('Studies'),
            path: '/site',
            redirectTo: 'dashboard',
            hideSideNav: true,
            canBeDefaultPage: true,
            appRoutes: [{ title: 'Studies', path: '/site', component: SiteApp }],
        },
    })), ((isAdmin || isUserAndSiteManager) && {
        siteManagement: {
            name: 'Sites',
            path: '/site-management',
            redirectTo: 'dashboard',
            hideSideNav: true,
            appRoutes: [{ title: 'Sites', path: '/site-management', component: SiteManagementApp }],
        },
    })), ((isAdmin || isDataAdmin) && {
        dataManagement: {
            name: translate('Transfer templates'),
            path: '/data',
            redirectTo: 'dashboard',
            hideSideNav: true,
            hideFromHeader: !isAdmin && !isDataUser && !isUserAndSiteManager && !isBuilder,
            appRoutes: [{ title: 'Data', path: '/data', component: DataManagementApp }],
        },
    })), (isAgentsAppEnabled &&
        hasAIAccess && {
        agents: {
            name: (_jsxs("div", { className: 'flex gap-2 items-center', children: [translate('AI Lab'), _jsx("div", { className: 'max-xl:hidden', children: _jsx(MedableAIIcon, { size: 18 }) })] })),
            path: `${process.env.AGENT_STUDIO_URL}/ai-lab`,
            redirectTo: `${process.env.AGENT_STUDIO_URL}/ai-lab`,
            hideSideNav: true,
            appRoutes: [],
            externalUrl: true,
            canBeDefaultPage: true,
        },
    })), { profile: {
            name: 'Your profile',
            path: '/profile',
            redirectTo: 'me',
            hideSideNav: true,
            hideFromHeader: true,
            canBeDefaultPage: true,
            appRoutes: [
                { title: 'Your profile', path: 'me', component: ProfilePage },
                { title: 'Notifications', path: 'notifications', component: NotificationsPage, hidden: !isAgentsAppEnabled },
            ],
        } }), (canUpdateWorkspace && {
        workspace: {
            name: 'Workspace settings',
            path: '/workspace',
            redirectTo: 'settings',
            hideSideNav: true,
            hideFromHeader: true,
            appRoutes: [{ title: 'Workspace settings', path: 'settings', component: WorkspaceSettingsPage }],
        },
    })), ((process.env.ENABLE_DEVELOPER_FLAGS === 'true' || process.env.ENABLE_DEVELOPER_FLAGS === 'route-only') && {
        flags: {
            name: 'Developer',
            path: '/developer',
            hideSideNav: true,
            hideFromHeader: true,
            appRoutes: [{ title: 'Developer flags', path: 'flags', component: DeveloperFlagsPage }],
        },
    }));
    return {
        appRoutes,
        defaultAppSection: Object.values(appRoutes).find((route) => route.canBeDefaultPage) || Object.values(appRoutes)[0],
    };
};
